// src/components/HeaderPro.js

import React, { useContext, useState, useEffect } from 'react';
import { useQuery }           from '@tanstack/react-query';
import { IoIosArrowBack }     from 'react-icons/io';
import { FaFilter }           from 'react-icons/fa';
import { TabContext }         from '../context/TabContext';
import { AuthContext }        from '../context/AuthContext';
import { ModalContext }       from '../context/ModalContext';
import { ModalHeaderContext } from '../context/ModalHeaderContext';
import API                    from '../api/client';
import ProDropdown            from './ProDropdown';
import { renderGenderIcon }   from '../utils/genderIcons';
import { IoSettingsOutline }  from 'react-icons/io5';
import { fetchProfessionalById } from '../api/queries';
import './Header.css';

export default function HeaderPro({ toggleFilterHeader }) {
  // ─── 1) Contextos y datos básicos ───────────────────────────────────
  const { user } = useContext(AuthContext);
  const professionalId = user?.professional_id;

  const { data: profInfo } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    enabled: !!professionalId,
    staleTime: 5 * 60_000,
  });
  const professionalName = profInfo?.nombre || '';
  const professionalLogo = profInfo?.logo   || '';

  const { activeTab } = useContext(TabContext);
  const { modalStacks, closeModal } = useContext(ModalContext);
  const { getHeaderActions, disabledMap } = useContext(ModalHeaderContext);

  // ─── 2) Hooks de estado para contar mascotas ──────────────────────────
  const [dogsCount, setDogsCount] = useState(null);
  const [catsCount, setCatsCount] = useState(null);

  useEffect(() => {
    if (activeTab !== 'prof_pets' || !professionalId) return;
    const token = localStorage.getItem('accessToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    (async () => {
      try {
        const [dRes, cRes] = await Promise.all([
          API.get(`/profesionales/${professionalId}/pets/`, {
            params: { species: 'perro', page_size: 1 }, headers
          }),
          API.get(`/profesionales/${professionalId}/pets/`, {
            params: { species: 'gato',  page_size: 1 }, headers
          })
        ]);
        setDogsCount(dRes.data.count);
        setCatsCount(cRes.data.count);
      } catch (err) {
        console.error('[HeaderPro] Error fetching counts:', err);
      }
    })();
  }, [activeTab, professionalId]);

  // ─── 3) Pilas de modales y detección de casos especiales ─────────────
  const currentStack = modalStacks[activeTab] || [];

  // A) Modal de edición de perfil profesional
  const proProfileEditModal = currentStack
    .slice().reverse()
    .find(m => m.metadata?.modalType === 'proProfileEdit');

  if (proProfileEditModal) {
    const { id } = proProfileEditModal;
    const headerActions = getHeaderActions(id) || {};
    const isDisabled = !!disabledMap[id];

    return (
      <header className="header" style={{ transform:'translateY(0)', opacity:1, backgroundColor:'#fff' }}>
        <div className="header-professional">
          {/* Izquierda: Cancelar edición */}
          <div className="header-left">
            <button
              className="headerPro-action-button"
              onClick={headerActions.onCancel}
              disabled={isDisabled}
            >
              Cancelar
            </button>
          </div>

          {/* Centro: Nombre del usuario */}
          <div className="header-center">
            <h2 className="professional-name-header">
              {user.first_name || user.username}
            </h2>
          </div>

          {/* Derecha: Guardar cambios */}
          <div className="header-right">
            <button
              className="headerPro-action-button"
              onClick={headerActions.onSave}
              disabled={isDisabled}
            >
              Guardar
            </button>
          </div>
        </div>
      </header>
    );
  }

  // A) ¿Está abierto el modal de Nueva Mascota?
  const newPetModal = currentStack.find(m => m.metadata?.isNewPetModal);


  // ─── 4) Si estamos en ModalNewPet, renderizamos el header específico ──
  if (newPetModal) {
    const { id, metadata } = newPetModal;
    const isDisabled = !!disabledMap[id];

    return (
      <header className="header" style={{ transform:'translateY(0)', opacity:1, backgroundColor:'#fff' }}>
        <div className="header-professional">
          {/* Izquierda: Cancelar */}
          <div className="header-left">
            <button
              className="headerPro-action-button"
              onClick={() => closeModal(activeTab)}
              disabled={isDisabled}
            >
              Cancelar
            </button>
          </div>
          {/* Centro: Título */}
          <div className="header-center">
            <h2 className="professional-name-header">Nueva mascota</h2>
          </div>
          {/* Derecha: Guardar */}
          <div className="header-right">
            <button
              className="headerPro-action-button"
              onClick={() => {
                if (typeof metadata.onSave === 'function') {
                  metadata.onSave();
                }
                //closeModal(activeTab);
              }}
              disabled={isDisabled}
            >
              Guardar
            </button>
          </div>
        </div>
      </header>
    );
  }

  // B) Modal de detalle de mascota
  const petModal = currentStack.slice().reverse().find(m => m.metadata?.petName);
  const petName = petModal?.metadata?.petName;
  const petSexo = petModal?.metadata?.petSexo;
  const hasModalOpen = currentStack.length > 0;
  const handleBack = () => closeModal(activeTab);

  if (petName) {
    return (
      <header className="header" style={{ transform:'translateY(0)', opacity:1, backgroundColor:'#fff' }}>
        <div className="header-pet-info">
          <button
            className="header-back-button"
            onClick={handleBack}
            aria-label="Volver"
          >
            <IoIosArrowBack className="header-back-icon" />
          </button>
          <div className="header-left">
            <img src="/logo192.png" alt="Pet_it Logo" className="logoLeft" />
          </div>
          <div className="header-center">
            <div className="petInfoContainer">
              <h2 className="petName">{petName}</h2>
              <span className="genderIcon">
                {petSexo ? renderGenderIcon(petSexo) : <span className="genderIconPlaceholder" />}
              </span>
            </div>
          </div>
          <div className="header-right">
            <button
              className="header-config-button"
              onClick={() => console.log('Abrir ajustes de la mascota', petModal.metadata.petId)}
              aria-label="Configuración"
            >
              <IoSettingsOutline className="header-config-icon" size={22} />
            </button>
          </div>
        </div>
      </header>
    );
  }

  // ─── Header estándar profesional / ProPetsPage / ProProfilePage ────
  return (
    <header className="header" style={{ transform:'translateY(0)', opacity:1, backgroundColor:'#fff' }}>
      <div className="header-professional">
        <div className="header-left">
          {activeTab === 'prof_profile' && professionalLogo && (
            <img
              src={professionalLogo}
              alt={professionalName}
              className="logoLeft"
              style={{ height:40, width:40, borderRadius:'50%' }}
            />
          )}
          {!hasModalOpen && toggleFilterHeader && activeTab === 'prof_pets' && (
            <button
              className="btn btn-link filter-toggle"
              onClick={toggleFilterHeader}
              aria-label="Filtrar mascotas"
            >
              <FaFilter className="filter-icon" />
            </button>
          )}
          {hasModalOpen && activeTab !== 'prof_pets' && (
            <button
              className="header-back-button"
              onClick={handleBack}
              aria-label="Volver"
            >
              <IoIosArrowBack className="header-back-icon" />
            </button>
          )}
        </div>

        <div className="header-center">
          {activeTab === 'prof_pets' && (
            <div className="pro-stats">
              <div>Perros: {dogsCount ?? '—'}</div>
              <div>Gatos:  {catsCount ?? '—'}</div>
            </div>
          )}
          {activeTab === 'prof_profile' && (
            <h2 className="professional-name-header">{professionalName}</h2>
          )}
        </div>

        <div className="header-right">
          <ProDropdown professional={user} />
        </div>
      </div>
    </header>
  );
}
